import { inject, Injectable } from '@angular/core';
import { TuiDay, TuiTime, TuiValueTransformer } from '@taiga-ui/cdk';

import { ION_TARGET_TIME_ZONE } from '../current-target-time-zone.provider';
import { fromIsoToTuiDay, fromIsoToTuiTime, toIsoDatetimeString } from './taiga-ui-date.fns';

/**
 * Transformer converting between an ISO-8601 string and a [{@Link TuiDay}, {@Link TuiTime}] tuple
 * using a specified IANA time zone for the (output) date string.
 *
 * The target time zone is specified by {@link ION_TARGET_TIME_ZONE}.
 *
 * Implements the Taiga UI value transformer interface to integrate with Taiga form controls (InputDateTime)
 * as internal representation of dates is using {@Link TuiDay} and {@Link TuiTime}
 */
@Injectable()
export class TimeZoneDateTimeTransformer implements TuiValueTransformer<
  [TuiDay, TuiTime | null] | null,
  string | null
> {
  /**
   * The IANA time zone used for formatting operations (output).
   */
  readonly timeZone: string = inject(ION_TARGET_TIME_ZONE);

  fromControlValue(controlValue: string | null): [TuiDay, TuiTime | null] | null {
    if (!controlValue) {
      return null;
    }

    const day = fromIsoToTuiDay(controlValue);
    const time = fromIsoToTuiTime(controlValue);

    return day ? [day, time ?? null] : null;
  }

  toControlValue(componentValue: [TuiDay, TuiTime | null] | null): string | null {
    if (!componentValue) {
      return null;
    }
    const [day, time] = componentValue;
    return toIsoDatetimeString(day, time, this.timeZone);
  }
}
